import { zodResolver } from '@hookform/resolvers/zod';
import { DEPARTMENTS, SessionUser } from '@certhon/models';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useIsOnline } from '@/features/offline/useSyncStatus';
import useAuthService from '@/hooks/use-auth-service';
import { CurrentUserPatch } from '@/services/AuthService';
import {
  NO_DEPARTMENT,
  ProfileFormValues,
  profileSchema,
} from '../settings.schemas';

function toFormValues(user: SessionUser): ProfileFormValues {
  return {
    name: user.name ?? '',
    department: user.department ?? NO_DEPARTMENT,
  };
}

/**
 * Name and department of the signed-in user, saved through `PATCH /me`.
 * The email address has its own form (`EmailChangeForm`): it only changes
 * after the verification mail is confirmed.
 */
export function ProfileForm({ user }: { user: SessionUser }) {
  const authService = useAuthService();
  const isOnline = useIsOnline();

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: toFormValues(user),
  });

  // A profile refresh (token renewal, save on another device) resets the
  // untouched form to the new server values.
  useEffect(() => {
    if (!form.formState.isDirty) {
      form.reset(toFormValues(user));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.name, user.department]);

  const onSubmit = async (values: ProfileFormValues) => {
    const patch: CurrentUserPatch = {};
    if (values.name !== user.name) patch.name = values.name;
    const department =
      values.department === NO_DEPARTMENT ? null : values.department;
    if (department !== user.department) patch.department = department;

    if (Object.keys(patch).length === 0) {
      form.reset(values);
      return;
    }

    try {
      const saved = await authService.patchCurrentUser(patch);
      form.reset(toFormValues(saved));
      toast.success('Your settings have been saved');
    } catch {
      toast.error('Something went wrong', {
        description:
          'Your changes have not been saved. Please try again later.',
      });
    }
  };

  const { isSubmitting, isDirty } = form.formState;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Full name</FormLabel>
              <FormControl>
                <Input autoComplete="name" placeholder="John Doe" {...field} />
              </FormControl>
              <FormDescription>
                Your full name is displayed to other users
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="department"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Department</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="No department" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value={NO_DEPARTMENT}>No department</SelectItem>
                  {DEPARTMENTS.map((department) => (
                    <SelectItem key={department} value={department}>
                      {department}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>
                Your department is used to set up relevant default filters
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={!isDirty || isSubmitting || !isOnline}
          >
            {isSubmitting && <Loader2 className="animate-spin" />}
            Save
          </Button>
        </div>
      </form>
    </Form>
  );
}
